import Button from '@rio-cloud/rio-uikit/Button';
import ExpanderPanel from '@rio-cloud/rio-uikit/ExpanderPanel';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import type { RootDispatch, RootState } from '../../../../configuration/setup/store';
import { isBookableProduct, isMultiLevelProduct, type Product } from '../../checkout/redux/types';
import { SUBSCRIPTION_WRITE } from '../../common/permissions/permissions';
import { getPermissions } from '../../common/permissions/redux/permissions.redux';
import { ProductTypedFormattedMessage } from '../../common/product/ProductTypedFormattedMessage';
import { getDefaultBookableLevel, isRatePlanBookable } from '../../common/product/productService';
import { ProductIcon } from '../../common/productImages/ProductIcon';
import { termedLevelRenewalActions } from '../../common/termedLevel/redux/termedLevelRenewal.redux';
import type { TermedLevel } from '../../common/termedLevel/types';
import { mapResourceTypeToProductType } from '../contracts/mapResourceTypeToProductType';
import { openCheckoutPopup } from '../serviceOverview/services/checkoutService';
import { GtmPromotion } from '../serviceOverview/services/gtmPromotion';
import { BookingOverviewContractTable } from './BookingOverviewContractTable';

interface OwnProps {
    contracts: TermedLevel[];
    products: Product[];
}

interface Props extends OwnProps {
    canConfigureRenewal: boolean;
    configureRenewal: (contract: TermedLevel, contracts: TermedLevel[]) => void;
}

function groupContractsByProduct(contracts: TermedLevel[]): TermedLevel[][] {
    return Array.from(
        contracts
            .reduce((groups, contract) => {
                const productContracts = groups.get(contract.productId) ?? [];
                productContracts.push(contract);
                groups.set(contract.productId, productContracts);
                return groups;
            }, new Map<string, TermedLevel[]>())
            .values()
    );
}

function getCheckoutTarget(product: Product | undefined): { level?: string } | undefined {
    if (isMultiLevelProduct(product)) {
        const bookableLevels = product.levels.filter(isRatePlanBookable).map(({ level }) => level);
        const level = getDefaultBookableLevel(bookableLevels);
        return level ? { level } : undefined;
    }

    return isBookableProduct(product)
        ? { level: getDefaultBookableLevel(product.level ? [product.level] : []) }
        : undefined;
}

function ProductPanelHeader({ contracts }: { contracts: TermedLevel[] }) {
    const { productId, productName, resourceType } = contracts[0];
    const productType = mapResourceTypeToProductType(resourceType);

    return (
        <div className='display-flex align-items-center'>
            <ProductIcon productId={productId} className='width-30 height-30 margin-right-10' />
            <span className='text-size-h4 text-bold'>{productName}</span>
            <span className='text-color-dark margin-left-5'>
                <ProductTypedFormattedMessage
                    productType={productType}
                    assetBasedId='intl-msg:marketplace.bookings.overview.product.asset.count'
                    userBasedId='intl-msg:marketplace.bookings.overview.product.user.count'
                    values={{ count: contracts.length }}
                />
            </span>
        </div>
    );
}

interface BookNowFooterProps {
    canBook: boolean;
    product: Product | undefined;
    productId: string;
}

function BookNowFooter({ canBook, product, productId }: BookNowFooterProps) {
    const checkoutTarget = getCheckoutTarget(product);
    const isDisabled = !canBook || !checkoutTarget;
    const buttonContent = <FormattedMessage id='intl-msg:marketplace.buybutton.bookNow' />;

    return (
        <div className='panel-footer position-sticky bottom-0 z-index-1 bg-white display-flex justify-content-end'>
            {isDisabled ? (
                <Button className='btn btn-primary' disabled>
                    {buttonContent}
                </Button>
            ) : (
                <BookNowLink productId={productId} level={checkoutTarget.level}>
                    {buttonContent}
                </BookNowLink>
            )}
        </div>
    );
}

function BookNowLink({ productId, level, children }: { productId: string; level?: string; children: ReactNode }) {
    const { callback, checkoutUrl } = openCheckoutPopup(productId, GtmPromotion.customerCenterAddMore, level);

    return (
        <Button as='a' className='btn btn-primary' href={checkoutUrl} onClick={callback}>
            {children}
        </Button>
    );
}

export function BookingOverviewProductList({ contracts, products, canConfigureRenewal, configureRenewal }: Props) {
    const groupedContracts = groupContractsByProduct(contracts);

    return (
        <div className='display-flex flex-column gap-10'>
            {groupedContracts.map((productContracts, index) => {
                const productId = productContracts[0].productId;
                const productType = mapResourceTypeToProductType(productContracts[0].resourceType);
                const product = products.find(({ sku }) => sku.toLowerCase() === productId.toLowerCase());

                return (
                    <ExpanderPanel
                        key={productId}
                        title={<ProductPanelHeader contracts={productContracts} />}
                        bsStyle='default'
                        bodyClassName='padding-0'
                        className='shadow-default'
                        open={index === 0}
                    >
                        <BookingOverviewContractTable
                            contracts={productContracts}
                            productType={productType}
                            canConfigureRenewal={canConfigureRenewal}
                            onConfigureRenewal={contract => configureRenewal(contract, contracts)}
                        />
                        <BookNowFooter canBook={canConfigureRenewal} product={product} productId={productId} />
                    </ExpanderPanel>
                );
            })}
        </div>
    );
}

const mapStateToProps = (state: RootState) => ({
    canConfigureRenewal: getPermissions(state)?.includes(SUBSCRIPTION_WRITE) ?? false,
});

const mapDispatchToProps = (dispatch: RootDispatch) => ({
    configureRenewal: (contract: TermedLevel, contracts: TermedLevel[]) => {
        dispatch(termedLevelRenewalActions.setTermedLevels(contracts));
        dispatch(
            termedLevelRenewalActions.selectForUpdate({
                contracts: [{ productId: contract.productId, resourceId: contract.resourceId }],
                renewsToLevel: contract.renewsToLevel,
                productId: contract.productId,
            })
        );
    },
});

export const BookingOverviewProductListContainer = connect(
    mapStateToProps,
    mapDispatchToProps
)(BookingOverviewProductList);
